// Project Readiness Score: the number is calculated live by quality-service from the epic's
// test cases, bugs and status (epicData.liveScore). The STR agent's saved score is only a
// fallback for an older quality-service, and the sample is the last resort. The Green/Amber/Red
// level is always derived from the live epic data, so it stays correct either way.

export const AUTOMATION_GREEN_THRESHOLD = 50;

// Shown only when the epic has no calculable score at all. Clearly labelled
// as a sample in the UI — never presented as the epic's real score.
export const SAMPLE_SCORE = {
  readinessScore: 72,
  testCaseVerificationScore: 90,
  bugSeverityIndexScore: 75,
  automationCoverageScore: 65,
  assuranceQualitativeScore: 65
};

// Illustrative AI insight shown, labelled as a sample, next to the sample score
export const SAMPLE_INSIGHT = {
  headline: 'Release readiness is at risk.',
  summary:
    'An open critical bug reports that charts in compare mode do not display data from the other data source, which may make comparisons incomplete or misleading. Low automation coverage reduces confidence that this issue will be caught by automated regression tests.',
  nextSteps: [
    'Triage and resolve the critical compare-mode bug before release.',
    'Add automated regression tests for charts using multiple data sources in compare mode.',
    'After the fix, verify that both sources appear correctly, then rerun the relevant scenarios.'
  ]
};

export const MAX_NEXT_STEPS = 3;

export const SCORE_COMPONENTS = [
  { key: 'testCaseVerificationScore', label: 'Test Verification', weight: 10 },
  { key: 'bugSeverityIndexScore', label: 'Bug Severity', weight: 40 },
  { key: 'automationCoverageScore', label: 'Automation', weight: 30 },
  { key: 'assuranceQualitativeScore', label: 'Assurance', weight: 20 }
];

// GREEN: no open critical (P0) or high (P1) bugs and automation above 50%.
// RED:   at least one open critical or high bug.
// AMBER: no open critical/high bugs, but automation at or below 50%.
export function getReadinessLevel({ openCriticalBugs, openHighBugs, automationCoverage }) {
  if (openCriticalBugs > 0 || openHighBugs > 0) return 'RED';
  if (automationCoverage > AUTOMATION_GREEN_THRESHOLD) return 'GREEN';
  return 'AMBER';
}

// The level is rule-based, so the explanation is too: it always names the open bugs or the
// automation figure that actually decided the level, never a model's guess.
export function getLevelReason(level, { openCriticalBugs, openHighBugs, automationCoverage }) {
  const plural = (count, noun) => `${count} open ${noun} bug${count === 1 ? '' : 's'}`;
  if (level === 'RED') {
    const bugs = [
      openCriticalBugs > 0 && plural(openCriticalBugs, 'P0'),
      openHighBugs > 0 && plural(openHighBugs, 'P1')
    ].filter(Boolean);
    return `Readiness is Red because this epic has ${bugs.join(' and ')}. Any open P0/P1 bug marks it Red, whatever the weighted score.`;
  }
  if (level === 'AMBER') {
    return `Readiness is Amber: no open P0/P1 bugs, but automation is ${automationCoverage}% and Green needs more than ${AUTOMATION_GREEN_THRESHOLD}%.`;
  }
  return `Readiness is Green: no open P0/P1 bugs and automation is ${automationCoverage}%.`;
}

// The insight comes from the STR agent. Recommendations are its prioritised actions, most important first.
// Missing text means the report was generated before AI insight existed, so those parts are simply omitted.
function getInsight(scoreData) {
  const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
  const nextSteps = (Array.isArray(scoreData.recommendations) ? scoreData.recommendations : [])
    .map((recommendation) => text(recommendation?.action))
    .filter(Boolean)
    .slice(0, MAX_NEXT_STEPS);
  const headline = text(scoreData.aiSummaryHeadline);
  const summary = text(scoreData.aiSummary);
  return { headline, summary, nextSteps };
}

const hasScore = (source) => Boolean(source) && typeof source.readinessScore === 'number';

// liveScore: calculated by quality-service from the live data. scoreData: the score the STR agent
// saved, which also carries the AI summary. scoreError: the saved-score request failed for any
// reason other than "not generated yet" — that only hides the AI summary when there is a live
// score, and shows no score (rather than a made-up one) when there isn't.
export function getProjectReadiness(scoreData, liveMetrics, scoreError = null, liveScore = null) {
  const level = getReadinessLevel(liveMetrics);
  if (scoreError && !hasScore(liveScore)) {
    return {
      isSample: false,
      isUnavailable: true,
      error: scoreError,
      score: null,
      level,
      components: SCORE_COMPONENTS.map((component) => ({ ...component, score: null })),
      insight: { headline: null, summary: null, nextSteps: [] },
      generatedAt: null
    };
  }

  const isLive = hasScore(liveScore);
  const isSample = !isLive && !hasScore(scoreData);
  const source = isLive ? liveScore : isSample ? SAMPLE_SCORE : scoreData;
  const savedReport = hasScore(scoreData) ? scoreData : null;
  return {
    isSample,
    isLive,
    isUnavailable: false,
    error: null,
    score: Math.round(source.readinessScore),
    level,
    components: SCORE_COMPONENTS.map((component) => ({
      ...component,
      score: typeof source[component.key] === 'number' ? Math.round(source[component.key]) : null
    })),
    insight: isSample ? SAMPLE_INSIGHT : getInsight(savedReport || {}),
    // The live score still shows when the saved report failed to load; this says why the AI summary is missing
    insightError: isLive ? scoreError : null,
    // When the AI text was written; the number itself is always current
    generatedAt: savedReport ? savedReport.generatedAt : null
  };
}
